import React from 'react';
import { Button } from '@this/src/components/shared/ui/inputs/button';
import { Link } from '@this/src/components/shared/ui/navigations/link';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@this/src/components/shared/ui/feedbacks/modal';
import { useAITTheme } from '@this/shared/ui/theme';
import { Datepicker } from '@this/shared/ui/inputs/datepicker';

export type CriticalError = { type: 'error' | 'message'; message: string } | { type: 'trip_id'; tripId: number };

interface Props {
  criticalErrors: CriticalError[];
  onCriticalErrorsClose: () => void;
  newUserMessageReceived: boolean;
  onNewUserMessageClose: () => void;
  cancelRequestReceived: { isOrderItemCancel: boolean; orderItemId?: number } | null;
  onCancelRequestClose: () => void;
  showSnoozeForm: boolean;
  snoozeTo: Date | undefined;
  snoozing: boolean;
  onSnoozeCancel: () => void;
  onSnoozeSave: () => void;
  onSnoozeToChange: (date: Date | undefined) => void;
}

const OrderItemStepWorkDialogAuxiliaryModals: React.FC<Props> = ({
  criticalErrors,
  onCriticalErrorsClose,
  newUserMessageReceived,
  onNewUserMessageClose,
  cancelRequestReceived,
  onCancelRequestClose,
  showSnoozeForm,
  snoozeTo,
  snoozing,
  onSnoozeCancel,
  onSnoozeSave,
  onSnoozeToChange
}) => {
  const { theme } = useAITTheme();

  return (
    <>
      <Modal open={criticalErrors.length > 0} onClose={onCriticalErrorsClose}>
        <ModalHeader>エラーが発生しました</ModalHeader>
        <ModalBody>
          {criticalErrors.map((error, index) =>
            error.type === 'error' ? (
              <p key={index} style={{ color: theme.redColor }}>
                {error.message}
              </p>
            ) : error.type === 'message' ? (
              <p key={index}>{error.message}</p>
            ) : error.type === 'trip_id' ? (
              <p key={index}>
                旅程番号：
                <Link href={`/arrangement/virtual_counter?trip_id=${error.tripId}`} target="_blank">
                  {error.tripId}
                </Link>
              </p>
            ) : null
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={onCriticalErrorsClose}>
            閉じる
          </Button>
        </ModalFooter>
      </Modal>
      <Modal open={newUserMessageReceived} onClose={onNewUserMessageClose}>
        <ModalHeader>新しいメッセージ</ModalHeader>
        <ModalBody>
          <p>ユーザーからの新しいメッセージがあります。内容をご確認ください。</p>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={onNewUserMessageClose}>
            確認
          </Button>
        </ModalFooter>
      </Modal>
      <Modal open={cancelRequestReceived !== null} onClose={onCancelRequestClose}>
        <ModalHeader>キャンセル依頼が入りました</ModalHeader>
        <ModalBody>
          <p>
            {cancelRequestReceived?.isOrderItemCancel
              ? 'この商品にキャンセル依頼が入りました。'
              : 'この旅程に一括キャンセル依頼が入りました。'}
          </p>
          <p>内容を確認してください。</p>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={onCancelRequestClose}>
            確認
          </Button>
        </ModalFooter>
      </Modal>
      <Modal open={showSnoozeForm} onClose={onSnoozeCancel}>
        <ModalHeader>スヌーズ</ModalHeader>
        <ModalBody>
          <div>
            <label style={{ display: 'block', marginBottom: '4px' }}>再通知日時</label>
            <Datepicker
              type="datetime-local"
              value={snoozeTo}
              onChange={d => onSnoozeToChange(d ?? undefined)}
              min={new Date()}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="sub" onClick={onSnoozeCancel}>
            キャンセル
          </Button>
          <Button color="primary" loading={snoozing} onClick={onSnoozeSave}>
            保存
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default OrderItemStepWorkDialogAuxiliaryModals;
